import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BrowserMultiFormatReader } from '@zxing/library';

interface BetterBarcodeScannerProps {
  onScan: (result: string) => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
  isActive: boolean;
}

const ScannerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const ScannerWrapper = styled.div`
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
`;

const ScanBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 250px;
  border: 2px solid #e4002b;
  border-radius: 8px;
  pointer-events: none;
  z-index: 10;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border: 3px solid #e4002b;
  }
  
  &::before {
    top: -3px;
    left: -3px;
    border-right: none;
    border-bottom: none;
  }
  
  &::after {
    bottom: -3px;
    right: -3px;
    border-left: none;
    border-top: none;
  }
`;

const StatusText = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  z-index: 10;
`;

const CancelButton = styled.button`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.75rem 1.5rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s;

  &:hover {
    background: #c82333;
    transform: translateX(-50%) translateY(-1px);
  }

  &:active {
    transform: translateX(-50%) translateY(0);
  }
`;

const Video = styled.video`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 8px;
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(220, 53, 69, 0.9);
  color: white;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  text-align: center;
  z-index: 15;
`;

const BetterBarcodeScanner: React.FC<BetterBarcodeScannerProps> = ({
  onScan,
  onError,
  onCancel,
  isActive
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isActive && !isScanning) {
      startScanning();
    } else if (!isActive && isScanning) {
      stopScanning();
    }

    return () => {
      stopScanning();
    };
  }, [isActive]);

  const startScanning = async () => {
    try {
      setError(null);
      setIsScanning(true);

      // Create a new code reader instance
      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      // Get video input devices
      const videoInputDevices = await codeReader.listVideoInputDevices();
      
      if (!videoInputDevices || videoInputDevices.length === 0) {
        throw new Error('No camera found on this device');
      }

      // Prefer back camera
      const selectedDeviceId = videoInputDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('rear')
      )?.deviceId || videoInputDevices[0].deviceId;

      // Start continuous decode from video device
      await codeReader.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current!,
        (result, error) => {
          if (result) {
            const text = result.getText();
            if (text && text.trim()) {
              onScan(text);
              stopScanning();
            }
          }
          // Ignore NotFoundException - it's normal while scanning
        }
      );

    } catch (err: any) {
      console.error('Scanner error:', err);
      setError(err.message || 'Failed to start camera');
      setIsScanning(false);
      if (onError) {
        onError(err);
      }
    }
  };

  const stopScanning = () => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    setIsScanning(false);
  };

  const handleCancel = () => {
    stopScanning();
    if (onCancel) {
      onCancel();
    }
  };

  if (!isActive) {
    return null;
  }

  return (
    <ScannerContainer>
      <ScannerWrapper>
        <StatusText>
          📷 Point camera at barcode
        </StatusText>
        
        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}
        
        <Video
          ref={videoRef}
          autoPlay
          playsInline
          muted
        />
        
        <ScanBox />
        
        <CancelButton onClick={handleCancel}>
          Cancel
        </CancelButton>
      </ScannerWrapper>
    </ScannerContainer>
  );
};

export default BetterBarcodeScanner;
